import React from "react";
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import "./SortableList.scss";

const directionOptionsEnum = {
  ROW: "row",
  COLUMN: "column",
} as const;

type DirectionOption =
  (typeof directionOptionsEnum)[keyof typeof directionOptionsEnum];

interface SortableListItem {
  id: UniqueIdentifier;
}

interface SortableListProps<TItem extends SortableListItem> {
  items: TItem[];
  onChange: (items: TItem[]) => void;
  renderItem: (item: TItem) => React.ReactNode;
  disabled?: boolean;
  direction?: DirectionOption;
}

const SortableList = <TItem extends SortableListItem>({
  items,
  onChange,
  renderItem,
  disabled,
  direction = directionOptionsEnum.COLUMN,
}: SortableListProps<TItem>) => {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={({ active, over }: DragEndEvent) => {
        if (over && active.id !== over?.id) {
          const activeIndex = items.findIndex(({ id }) => id === active.id);
          const overIndex = items.findIndex(({ id }) => id === over.id);

          onChange(arrayMove(items, activeIndex, overIndex));
        }
      }}
    >
      <SortableContext items={items} disabled={disabled}>
        <ul
          className={`sortable-list ${direction} ${disabled ? "disabled" : ""}`}
        >
          {items.map((item) => (
            <React.Fragment key={item.id}>{renderItem(item)}</React.Fragment>
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
};

export default SortableList;
